import React, { useState, useEffect, useRef } from 'react';

interface FlowStep {
  id: string;
  label: string;
  type: 'user' | 'frontend' | 'api' | 'database' | 'storage';
  active?: boolean;
  data?: any;
}

interface Flow {
  name: string;
  steps: FlowStep[];
}

export default function MobileArchitectureView() {
  const [activeFlow, setActiveFlow] = useState<string>('create');
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [flowData, setFlowData] = useState<any>({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [showLegend, setShowLegend] = useState(false);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  const flows: Record<string, Flow> = {
    create: {
      name: 'Create Receiver',
      steps: [
        { id: 'user-input', label: 'Enter name', type: 'user' },
        { id: 'frontend', label: 'Process input', type: 'frontend' },
        { id: 'generate', label: 'Generate slug', type: 'frontend' },
        { id: 'api-call', label: 'API request', type: 'api' },
        { id: 'd1-write', label: 'Save to DB', type: 'database' },
        { id: 'response', label: 'Return link', type: 'api' },
        { id: 'localstorage', label: 'Store locally', type: 'frontend' }
      ]
    },
    upload: {
      name: 'Upload Files',
      steps: [
        { id: 'select', label: 'Select files', type: 'user' },
        { id: 'modal', label: 'Show modal', type: 'frontend' },
        { id: 'formdata', label: 'Prepare data', type: 'frontend' },
        { id: 'upload-api', label: 'Upload API', type: 'api' },
        { id: 'r2-store', label: 'Store in R2', type: 'storage' },
        { id: 'd1-meta', label: 'Save metadata', type: 'database' },
        { id: 'success', label: 'Confirm', type: 'api' },
        { id: 'share-modal', label: 'Share options', type: 'frontend' }
      ]
    },
    display: {
      name: 'Display Files',
      steps: [
        { id: 'page-load', label: 'Load page', type: 'user' },
        { id: 'pages-func', label: 'Pages function', type: 'frontend' },
        { id: 'get-link', label: 'Get link data', type: 'api' },
        { id: 'd1-query', label: 'Query DB', type: 'database' },
        { id: 'list-files', label: 'List files API', type: 'api' },
        { id: 'r2-list', label: 'List from R2', type: 'storage' },
        { id: 'generate-html', label: 'Build page', type: 'frontend' },
        { id: 'render', label: 'Show files', type: 'user' }
      ]
    },
    download: {
      name: 'Download',
      steps: [
        { id: 'click', label: 'Click file', type: 'user' },
        { id: 'download-api', label: 'Request file', type: 'api' },
        { id: 'r2-fetch', label: 'Get from R2', type: 'storage' },
        { id: 'stream', label: 'Stream data', type: 'api' },
        { id: 'save', label: 'Save file', type: 'user' }
      ]
    }
  };

  const flowKeys = Object.keys(flows);

  useEffect(() => {
    if (isPlaying && activeFlow) {
      const flow = flows[activeFlow];
      if (flow && currentStep < flow.steps.length) {
        const timer = setTimeout(() => {
          updateFlowData(activeFlow, currentStep);
          setCurrentStep(currentStep + 1);
        }, 1000);
        return () => clearTimeout(timer);
      } else if (currentStep >= flow?.steps.length) {
        setIsPlaying(false);
      }
    }
  }, [isPlaying, activeFlow, currentStep]);

  const updateFlowData = (flow: string, step: number) => {
    const mockData: any = {
      create: [
        { input: 'Wedding Photos' },
        { processing: true },
        { slug: 'xyz123/wedding' },
        { sending: true },
        { saved: 'ID: uuid-123' },
        { url: 'fastfiles.app/...' },
        { cached: true }
      ],
      upload: [
        { files: '2 images' },
        { modal: 'open' },
        { size: '4.2 MB' },
        { uploading: '...' },
        { stored: 'R2' },
        { metadata: 'saved' },
        { status: '✓' },
        { sharing: true }
      ],
      display: [
        { loading: true },
        { params: 'xyz123' },
        { fetching: true },
        { data: 'found' },
        { listing: true },
        { files: '2 found' },
        { html: 'built' },
        { rendered: '✓' }
      ],
      download: [
        { file: 'IMG_001.jpg' },
        { requesting: true },
        { fetching: true },
        { streaming: true },
        { saved: '✓' }
      ]
    };

    setFlowData((prev: any) => ({
      ...prev,
      [`${flow}-${step}`]: mockData[flow]?.[step] || {}
    }));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipe();
  };

  const handleSwipe = () => {
    const swipeDistance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      const currentIndex = flowKeys.indexOf(activeFlow);

      if (swipeDistance > 0 && currentIndex < flowKeys.length - 1) {
        // Swipe left - next flow
        changeFlow(flowKeys[currentIndex + 1]);
      } else if (swipeDistance < 0 && currentIndex > 0) {
        // Swipe right - previous flow
        changeFlow(flowKeys[currentIndex - 1]);
      }
    }
  };

  const changeFlow = (flowName: string) => {
    setActiveFlow(flowName);
    setCurrentStep(0);
    setFlowData({});
    setIsPlaying(false);
    setExpandedStep(null);
  };

  const toggleStep = (index: number) => {
    setExpandedStep(expandedStep === index ? null : index);
  };

  const getStepStatus = (index: number) => {
    if (index < currentStep) return 'completed';
    if (index === currentStep && isPlaying) return 'active';
    return 'pending';
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'user': return 'bg-blue-500';
      case 'frontend': return 'bg-indigo-500';
      case 'api': return 'bg-yellow-500';
      case 'database': return 'bg-purple-500';
      case 'storage': return 'bg-pink-500';
      default: return 'bg-gray-500';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'user': return '👤';
      case 'frontend': return '💻';
      case 'api': return '🔌';
      case 'database': return '💾';
      case 'storage': return '📦';
      default: return '📍';
    }
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setFlowData({});
    setIsPlaying(true);
    setExpandedStep(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 pb-20">
      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        .animate-slide-down {
          animation: slideDown 0.3s ease-out;
        }
        .animate-pulse-custom {
          animation: pulse 1.5s infinite;
        }
        .timeline-line {
          background: linear-gradient(180deg,
            transparent 0%,
            #e5e7eb 10%,
            #e5e7eb 90%,
            transparent 100%
          );
        }
      `}</style>

      {/* Header */}
      <div className="sticky top-0 bg-white shadow-sm z-10">
        <div className="px-4 py-3">
          <h1 className="text-xl font-bold text-gray-800">FastFile Architecture</h1>
          <p className="text-sm text-gray-600 mt-1">Tap steps to explore • Swipe to change flow</p>
        </div>
      </div>

      {/* Flow Tabs */}
      <div className="bg-white border-b">
        <div className="flex overflow-x-auto px-4 py-2 gap-2">
          {flowKeys.map((key) => (
            <button
              key={key}
              onClick={() => changeFlow(key)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap font-medium text-sm transition-all ${
                activeFlow === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {flows[key].name}
            </button>
          ))}
        </div>

        {/* Flow Indicators */}
        <div className="flex justify-center gap-2 py-2">
          {flowKeys.map((key) => (
            <div
              key={key}
              className={`h-1 w-8 rounded-full transition-all ${
                activeFlow === key ? 'bg-blue-600 w-12' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Timeline View */}
      <div
        className="px-4 py-6"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 timeline-line" />

          {/* Steps */}
          {flows[activeFlow].steps.map((step, index) => {
            const status = getStepStatus(index);
            const data = flowData[`${activeFlow}-${index}`];

            return (
              <div
                key={step.id}
                className={`relative mb-6 animate-slide-down`}
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => toggleStep(index)}
              >
                <div className="flex items-start gap-4">
                  {/* Step Indicator */}
                  <div className="relative z-10">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-xl shadow-lg transition-all ${
                        getTypeColor(step.type)
                      } ${status === 'active' ? 'animate-pulse-custom' : ''} ${
                        status === 'completed' ? 'ring-4 ring-green-400 ring-opacity-50' : ''
                      }`}
                    >
                      {status === 'completed' ? '✓' : getTypeIcon(step.type)}
                    </div>
                    {status === 'active' && (
                      <div className="absolute inset-0 rounded-full bg-white opacity-25 animate-ping" />
                    )}
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 bg-white rounded-lg shadow-sm p-4 transition-all">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-800">{step.label}</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {step.type.charAt(0).toUpperCase() + step.type.slice(1)} Layer
                        </p>
                      </div>
                      {status === 'completed' && (
                        <span className="text-green-500 text-sm">✓</span>
                      )}
                      {status === 'active' && (
                        <span className="text-blue-500 text-sm animate-pulse">●</span>
                      )}
                    </div>

                    {/* Expandable Details */}
                    {(expandedStep === index || data) && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        {data && (
                          <div className="text-xs font-mono text-gray-600 bg-gray-50 rounded p-2">
                            {JSON.stringify(data, null, 2)}
                          </div>
                        )}
                        {expandedStep === index && (
                          <div className="text-xs text-gray-500 mt-2">
                            <p>Step {index + 1} of {flows[activeFlow].steps.length}</p>
                            <p className="mt-1">ID: {step.id}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress Bar */}
        {isPlaying && (
          <div className="mt-6 bg-white rounded-lg p-4 shadow-sm">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Progress</span>
              <span>{currentStep} / {flows[activeFlow].steps.length}</span>
            </div>
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${(currentStep / flows[activeFlow].steps.length) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setShowLegend(!showLegend)}
              className="text-sm text-gray-600 underline"
            >
              {showLegend ? 'Hide' : 'Show'} Legend
            </button>
            <div className="flex gap-2">
              <button
                onClick={handleRestart}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium"
              >
                Restart
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium min-w-[80px]"
              >
                {isPlaying ? 'Pause' : 'Play'}
              </button>
            </div>
          </div>

          {/* Legend */}
          {showLegend && (
            <div className="border-t pt-3 grid grid-cols-3 gap-2 text-xs">
              {[
                { type: 'user', label: 'User', color: 'bg-blue-500' },
                { type: 'frontend', label: 'Frontend', color: 'bg-indigo-500' },
                { type: 'api', label: 'API', color: 'bg-yellow-500' },
                { type: 'database', label: 'Database', color: 'bg-purple-500' },
                { type: 'storage', label: 'Storage', color: 'bg-pink-500' }
              ].map(item => (
                <div key={item.type} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full ${item.color}`} />
                  <span className="text-gray-700">{item.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}